import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AuthCallback - Handles OAuth redirect from Emergent Auth (kept for backwards compatibility)
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/login', { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await fetch(`${API_URL}/api/auth/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) throw new Error('Failed to exchange session');

        const result = await response.json();
        if (!result.success) throw new Error(result.detail || 'Exchange failed');

        localStorage.setItem('session_token', result.session_token);
        navigate('/dashboard', { replace: true, state: { user: result.user } });

      } catch (error) {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, location.hash]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent mx-auto mb-4"></div>
        <p className="text-gray-600">Anmeldung wird verarbeitet...</p>
      </div>
    </div>
  );
}

/**
 * LoginPage - Email/Password login with Password Reset
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if there's a reset token in URL
  const resetTokenFromUrl = searchParams.get('reset_token');
  
  // Modes: 'login', 'register', 'forgot', 'reset'
  const [mode, setMode] = useState(resetTokenFromUrl ? 'reset' : 'login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    resetToken: resetTokenFromUrl || ''
  });

  // Update reset token if URL changes
  useEffect(() => {
    if (resetTokenFromUrl) {
      setMode('reset');
      setFormData(prev => ({ ...prev, resetToken: resetTokenFromUrl }));
    }
  }, [resetTokenFromUrl]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Falls ein Konto mit dieser E-Mail existiert, haben wir Ihnen einen Link zum Zurücksetzen gesendet.');
      } else {
        throw new Error(data.detail || 'Fehler beim Senden der E-Mail');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwörter stimmen nicht überein');
      }
      if (formData.password.length < 6) {
        throw new Error('Passwort muss mindestens 6 Zeichen haben');
      }

      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: formData.resetToken,
          new_password: formData.password
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Zurücksetzen des Passworts');
      }

      setSuccess('Passwort wurde erfolgreich geändert! Sie können sich jetzt anmelden.');
      
      // Clear URL params and switch to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { replace: true });
        setMode('login');
        setFormData({ email: '', password: '', name: '', confirmPassword: '', resetToken: '' });
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Email validation (since browser validation is disabled with noValidate)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email || !emailRegex.test(formData.email.trim())) {
        throw new Error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      }
      if (!formData.password) {
        throw new Error('Bitte geben Sie ein Passwort ein');
      }

      if (mode === 'register') {
        // Validate
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwörter stimmen nicht überein');
        }
        if (formData.password.length < 6) {
          throw new Error('Passwort muss mindestens 6 Zeichen haben');
        }
        if (!formData.name.trim()) {
          throw new Error('Bitte geben Sie Ihren Namen ein');
        }

        // Register
        const response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            name: formData.name
          })
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Registrierung fehlgeschlagen');
        }

        localStorage.setItem('session_token', data.session_token);
        navigate('/dashboard', { replace: true, state: { user: data.user } });

      } else {
        // Login
        let response;
        try {
          response = await fetch(`${API_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: formData.email.trim(),
              password: formData.password
            })
          });
        } catch (fetchErr) {
          throw new Error('Verbindung zum Server fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung.');
        }

        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseErr) {
          if (text.includes('<html') || text.includes('<!DOCTYPE')) {
            throw new Error('Server nicht erreichbar (HTML-Antwort statt JSON). Bitte prüfen Sie die URL.');
          }
          throw new Error(`Server-Fehler (Status ${response.status}). Antwort: ${text.substring(0, 100)}`);
        }
        
        if (!response.ok) {
          throw new Error(data.detail || 'Anmeldung fehlgeschlagen');
        }

        localStorage.setItem('session_token', data.session_token);
        navigate('/dashboard', { replace: true, state: { user: data.user } });
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'register': return 'Kostenloses Konto erstellen';
      case 'forgot': return 'Passwort vergessen';
      case 'reset': return 'Neues Passwort erstellen';
      default: return 'Willkommen zurück';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/wisecall-logo.png" alt="Wisecall" className="h-20 w-auto mx-auto mb-4" />
          <p className="text-gray-500 mt-2">{getTitle()}</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Forgot Password Form */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} noValidate className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="ihre@email.de"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                required
                data-testid="forgot-email-input"
              />
              <p className="mt-2 text-sm text-gray-500">
                Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Senden...
                </span>
              ) : (
                'Link senden'
              )}
            </button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="text-emerald-600 font-medium hover:underline text-sm"
              >
                ← Zurück zur Anmeldung
              </button>
            </div>
          </form>
        )}

        {/* Reset Password Form */}
        {mode === 'reset' && (
          <form onSubmit={handleResetPassword} noValidate className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                required
                minLength={6}
                data-testid="reset-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Passwort bestätigen</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                required
                minLength={6}
                data-testid="reset-confirm-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Speichern...
                </span>
              ) : (
                'Passwort speichern'
              )}
            </button>
          </form>
        )}

        {/* Login/Register Form */}
        {(mode === 'login' || mode === 'register') && (
          <>
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Max Mustermann"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ihre@email.de"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>

              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passwort bestätigen</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
              )}

              {/* Forgot Password Link - only show in login mode */}
              {mode === 'login' && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                    className="text-sm text-emerald-600 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Passwort vergessen?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                data-testid="submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Bitte warten...
                  </span>
                ) : (
                  mode === 'login' ? 'Anmelden' : 'Registrieren'
                )}
              </button>
            </form>

            {/* Toggle Login/Register */}
            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                {mode === 'login' ? (
                  <>
                    Noch kein Konto?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('register'); setError(''); setSuccess(''); }}
                      className="text-emerald-600 font-medium hover:underline"
                      data-testid="switch-to-register"
                    >
                      Jetzt registrieren
                    </button>
                  </>
                ) : (
                  <>
                    Bereits registriert?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                      className="text-emerald-600 font-medium hover:underline"
                      data-testid="switch-to-login"
                    >
                      Zur Anmeldung
                    </button>
                  </>
                )}
              </p>
            </div>

            {/* Features */}
            {mode === 'register' && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center mb-3">Inklusive im kostenlosen Tarif:</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span> 60 Minuten gratis
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span> 1 KI-Agent
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span> Email & SMS Tools
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span> Wissensbasis
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <p className="text-xs text-gray-400 text-center mt-6">
          Mit der Anmeldung stimmen Sie unseren Nutzungsbedingungen zu.
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
